import { DataResult, DataStream, WidgetSettingsType } from '@thingslog/repositories';
import { DataResultParseStrategy } from './DataResultParseStrategy';
import {
  DataResultParsed,
  DataResultParseDataPoint,
  DataResultParseStream,
} from '../DataResultParsed';
import { createUnitTooltipFormatter, formatValueWithUnit } from '../metadata';
import { t } from 'i18next';

type TransmissionMetric = 'days' | 'transmissions' | 'count' | 'transmitting';

const transmissionMetricConfig: Record<
  TransmissionMetric,
  {
    color: string;
    dataIndex: number;
    label: string;
    labelKey: string;
    unit?: string;
    valueType?: 'boolean';
  }
> = {
  days: {
    color: '#14b8a6',
    dataIndex: 0,
    label: 'Days',
    labelKey: 'dashboard_days',
    unit: 'days',
  },
  transmissions: {
    color: '#f97316',
    dataIndex: 1,
    label: 'Transmissions',
    labelKey: 'dashboard_transmissions',
  },
  count: {
    color: '#3b82f6',
    dataIndex: 2,
    label: 'Count',
    labelKey: 'dashboard_count',
  },
  transmitting: {
    color: '#8b5cf6',
    dataIndex: 3,
    label: 'Transmitting',
    labelKey: 'dashboard_transmitting',
    valueType: 'boolean',
  },
};

const transmissionMetrics = Object.keys(transmissionMetricConfig) as TransmissionMetric[];

const parseSelectedMetrics = (value?: string): TransmissionMetric[] => {
  const metrics = value?.split(',').filter((metric: string): metric is TransmissionMetric => {
    return transmissionMetrics.includes(metric as TransmissionMetric);
  });

  return metrics && metrics.length > 0 ? metrics : ['days'];
};

const resolveMetricValue = (stream: DataStream, metric: TransmissionMetric): number | null => {
  if (stream.type !== 'series') {
    return null;
  }

  const config = transmissionMetricConfig[metric];
  const value = stream.data[config.dataIndex] ?? stream.metaData[metric];
  const numericValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

export class ApiDevicesTransmissionsDataResultParseStrategy implements DataResultParseStrategy {
  public parse(dataResult: DataResult, widgetType: WidgetSettingsType): DataResultParsed {
    const selectedMetrics = parseSelectedMetrics(dataResult.metaData.transmissionMetrics);
    const daysUnit = t('dashboard_unit_days', 'days');
    const yAxisUnit =
      selectedMetrics.length === 1 && transmissionMetricConfig[selectedMetrics[0]].unit
        ? daysUnit
        : undefined;

    const parsed: DataResultParsed = {
      dataStreams: [],
      xAxis: {
        show: true,
        dataKey: widgetType === 'table' ? 'deviceLabel' : 'deviceNumber',
        name: t('devices', 'Devices'),
        formatter: (value: string) => value,
      },
      yAxis: {
        show: true,
        formatter: (value: number) => formatValueWithUnit(value, yAxisUnit),
      },
      legend: {
        show: true,
      },
      tooltip: {
        formatter: createUnitTooltipFormatter(),
        labelFormatter: (value: string | number, dataPoint?: DataResultParseDataPoint): string => {
          return dataPoint?.deviceName ? `${value} - ${dataPoint.deviceName}` : String(value);
        },
      },
    };

    const dataStream: DataResultParseStream = {
      data: [],
      visualization: selectedMetrics.map((metric: TransmissionMetric) => {
        const config = transmissionMetricConfig[metric];

        return {
          dataKey: metric,
          fill: config.color,
          innerRadius: metric === 'days' ? 70 : 48,
          outerRadius: metric === 'days' ? 90 : 68,
          name: t(config.labelKey, config.label),
          nameKey: 'deviceLabel',
          unit: config.unit ? daysUnit : undefined,
          valueType: config.valueType,
        };
      }),
    };

    dataResult.streams.forEach((stream: DataStream) => {
      if (stream.type !== 'series') return;
      const deviceNumber = stream.metaData.deviceNumber ?? '';
      const metricValues = selectedMetrics.reduce(
        (
          values: Partial<Record<TransmissionMetric, number>>,
          metric: TransmissionMetric
        ): Partial<Record<TransmissionMetric, number>> => {
          const value = resolveMetricValue(stream, metric);

          if (value !== null) {
            values[metric] = value;
          }

          return values;
        },
        {}
      );

      if (Object.keys(metricValues).length === 0) {
        return;
      }

      dataStream.data.push({
        ...stream.metaData,
        deviceLabel: stream.metaData.deviceName
          ? `${deviceNumber} - ${stream.metaData.deviceName}`
          : deviceNumber,
        ...metricValues,
      });
    });

    parsed.dataStreams.push(dataStream);

    return parsed;
  }
}
